 
import { DragEvent } from 'react';
import {
  convertToFilesArray,
  createFileWithPath,
  FileWithPath,
} from './fileWithPath';
import { debugLog } from 'shared/logger/debugLog';
import { logError } from 'setup/logger/logError';

export interface DataTransferDragSource {
  dataTransfer: DataTransfer | null;
}

export function isExternalFilesDrag(e: DragEvent<Element>): boolean {
  return e.dataTransfer?.types.includes('Files') ?? false;
}

export function getFilesFromDragEvent(e: DataTransferDragSource): File[] {
  return Array.from(e.dataTransfer?.files || []);
}

export function hasDirectoriesInFiles(files: File[]): boolean {
  return files.some((file) => {
    const path = file.webkitRelativePath;
    return path && path.includes('/');
  });
}

/**
 * Browsers often represent a dropped directory as a 0-byte File without a MIME type.
 * May false-positive on legitimately empty files — use only on drop, not file picker.
 */
export function isLikelyDroppedDirectory(file: File): boolean {
  return file.size === 0 && file.type === '';
}

type DataTransferEntryScan = {
  hasDirectory: boolean;
  hasFileEntry: boolean;
  hasUnknownFileItem: boolean;
};

function scanFileItemsInDataTransfer(
  dataTransfer: DataTransfer | null
): DataTransferEntryScan | null {
  if (!dataTransfer?.items?.length) {
    return null;
  }

  const scan: DataTransferEntryScan = {
    hasDirectory: false,
    hasFileEntry: false,
    hasUnknownFileItem: false,
  };

  for (let i = 0; i < dataTransfer.items.length; i++) {
    const item = dataTransfer.items[i];
    if (item.kind !== 'file') {
      continue;
    }

    const entry = item.webkitGetAsEntry?.() ?? null;
    if (entry) {
      if (entry.isDirectory) {
        scan.hasDirectory = true;
      } else if (entry.isFile) {
        scan.hasFileEntry = true;
      }
      continue;
    }

    scan.hasUnknownFileItem = true;
  }

  if (
    !scan.hasDirectory &&
    !scan.hasFileEntry &&
    !scan.hasUnknownFileItem
  ) {
    return null;
  }

  return scan;
}

/**
 * Best-effort directory detection during dragenter/dragover.
 * Returns null when the browser does not expose enough metadata (keep current UX).
 */
export function detectDirectoryInDataTransferBestEffort(
  dataTransfer: DataTransfer | null
): boolean | null {
  const scan = scanFileItemsInDataTransfer(dataTransfer);
  if (scan === null) {
    return null;
  }

  if (scan.hasDirectory) {
    return true;
  }

  if (scan.hasFileEntry && !scan.hasUnknownFileItem) {
    return false;
  }

  return null;
}

function hasDirectoryInDroppedFiles(files: File[]): boolean {
  return (
    hasDirectoriesInFiles(files) || files.some(isLikelyDroppedDirectory)
  );
}

/**
 * Definitive directory detection on drop — evaluates the whole DataTransfer before upload.
 */
export async function detectDirectoryInDropEvent(
  e: DataTransferDragSource
): Promise<boolean> {
  const scan = scanFileItemsInDataTransfer(e.dataTransfer);
  if (scan?.hasDirectory) {
    return true;
  }

  const droppedFiles = getFilesFromDragEvent(e);
  if (hasDirectoryInDroppedFiles(droppedFiles)) {
    return true;
  }

  if (scan?.hasFileEntry && !scan.hasUnknownFileItem) {
    return false;
  }

  if (!e.dataTransfer?.items?.length) {
    return false;
  }

  try {
    const { hasDirectories, emptyDirectoryPaths } = await getDroppedItems(e);
    return hasDirectories || emptyDirectoryPaths.length > 0;
  } catch {
    return false;
  }
}

export function isOutsideElement(
  e: DragEvent<HTMLElement>,
  element: HTMLElement
): boolean {
  const rect = element.getBoundingClientRect();
  return (
    e.clientX < rect.left ||
    e.clientX > rect.right ||
    e.clientY < rect.top ||
    e.clientY > rect.bottom
  );
}

async function readFileFromEntry(
  fileEntry: FileSystemFileEntry,
  relativePath: string
): Promise<FileWithPath> {
  const file = await new Promise<File>((resolve, reject) => {
    fileEntry.file(resolve, reject);
  });

  return createFileWithPath(file, relativePath);
}

async function readAllEntriesFromDirectory(
  reader: FileSystemDirectoryReader
): Promise<FileSystemEntry[]> {
  const allEntries: FileSystemEntry[] = [];
  let entriesBatch: FileSystemEntry[];

  do {
    entriesBatch = await new Promise<FileSystemEntry[]>((resolve) => {
      reader.readEntries(resolve);
    });
    allEntries.push(...entriesBatch);
  } while (entriesBatch.length > 0);

  return allEntries;
}

function buildRelativePath(parentPath: string, entryName: string): string {
  return parentPath ? `${parentPath}/${entryName}` : entryName;
}

interface ProcessedEntryResult {
  files: FileWithPath[];
  isDirectory: boolean;
  /** Paths of directories (this one and/or its descendants) that contain no files. */
  emptyDirectoryPaths: string[];
}

async function processFileSystemEntry(
  entry: FileSystemEntry,
  parentPath: string
): Promise<ProcessedEntryResult> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    const relativePath = buildRelativePath(parentPath, entry.name);
    const fileWithPath = await readFileFromEntry(fileEntry, relativePath);
    return { files: [fileWithPath], isDirectory: false, emptyDirectoryPaths: [] };
  }

  if (entry.isDirectory) {
    const directoryEntry = entry as FileSystemDirectoryEntry;
    const reader = directoryEntry.createReader();
    const childEntries = await readAllEntriesFromDirectory(reader);
    const newPath = buildRelativePath(parentPath, entry.name);

    const childResults = await Promise.all(
      childEntries.map((childEntry) =>
        processFileSystemEntry(childEntry, newPath)
      )
    );

    const files = childResults.flatMap((result) => result.files);
    const emptyDirectoryPaths = childResults.flatMap(
      (result) => result.emptyDirectoryPaths
    );

    // No files anywhere under this directory (empty leaf, or only empty
    // subdirectories) - record its own path so it still gets created.
    if (files.length === 0) {
      emptyDirectoryPaths.push(newPath);
    }

    return { files, isDirectory: true, emptyDirectoryPaths };
  }

  return { files: [], isDirectory: false, emptyDirectoryPaths: [] };
}

interface CollectedItem {
  entry: FileSystemEntry | null;
  file: File | null;
}

function collectItemsSynchronously(
  dataTransfer: DataTransfer | null
): CollectedItem[] {
  const collected: CollectedItem[] = [];

  if (!dataTransfer?.items) {
    return collected;
  }

  const items = dataTransfer.items;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind !== 'file') {
      continue;
    }

    const entry = item.webkitGetAsEntry?.() ?? null;
    const file = entry ? null : item.getAsFile();

    if (entry === null && file === null) {
      continue;
    }

    collected.push({ entry, file });
  }

  return collected;
}

async function processCollectedItems(
  items: CollectedItem[]
): Promise<{
  files: FileWithPath[];
  containsDirectories: boolean;
  emptyDirectoryPaths: string[];
}> {
  const results = await Promise.all(
    items.map(async ({ entry, file }) => {
      if (entry) {
        return processFileSystemEntry(entry, '');
      }

      if (file) {
        return {
          files: [createFileWithPath(file, file.name)],
          isDirectory: false,
          emptyDirectoryPaths: [],
        };
      }

      throw new Error('Collected item must have either entry or file');
    })
  );

  return {
    files: results.flatMap((result) => result.files),
    containsDirectories: results.some((result) => result.isDirectory),
    emptyDirectoryPaths: results.flatMap((result) => result.emptyDirectoryPaths),
  };
}

export async function getDroppedItems(
  e: DataTransferDragSource
): Promise<{
  files: File[];
  hasDirectories: boolean;
  emptyDirectoryPaths: string[];
}> {
  if (!e.dataTransfer?.items?.length) {
    const fallbackFiles = getFilesFromDragEvent(e);
    return {
      files: fallbackFiles,
      hasDirectories: hasDirectoriesInFiles(fallbackFiles),
      emptyDirectoryPaths: [],
    };
  }

  try {
    const collectedItems = collectItemsSynchronously(e.dataTransfer);

    debugLog(
      `getDroppedItems: collected ${collectedItems.length} items synchronously (entries: ${collectedItems.filter((i) => i.entry).length}, files: ${collectedItems.filter((i) => i.file).length})`
    );

    const {
      files: collectedFilesWithPath,
      containsDirectories,
      emptyDirectoryPaths,
    } = await processCollectedItems(collectedItems);

    if (collectedFilesWithPath.length === 0 && emptyDirectoryPaths.length === 0) {
      const fallbackFiles = getFilesFromDragEvent(e);
      return {
        files: fallbackFiles,
        hasDirectories: hasDirectoriesInFiles(fallbackFiles),
        emptyDirectoryPaths: [],
      };
    }

    debugLog(
      `getDroppedItems: processed ${collectedFilesWithPath.length} files, hasDirectories: ${containsDirectories}, emptyDirectories: ${emptyDirectoryPaths.length}`
    );

    const files = convertToFilesArray(collectedFilesWithPath);

    return {
      files,
      hasDirectories: containsDirectories,
      emptyDirectoryPaths,
    };
  } catch (error) {
    logError('Error reading external dropped items:', error);
    const fallbackFiles = getFilesFromDragEvent(e);
    return {
      files: fallbackFiles,
      hasDirectories: hasDirectoriesInFiles(fallbackFiles),
      emptyDirectoryPaths: [],
    };
  }
}
